/**
 * Deal lifecycle — the rail from the demo, but as an explicit state machine.
 *
 * In the prototype `status` was a mutable string anyone could overwrite
 * (`d.status = 'settled'`). Here a transition is only legal if it is declared
 * below, which is what stops a deal being disbursed or settled twice.
 */

export const DEAL_TYPES = ['INVOICE', 'ORDER'] as const;
export type DealType = (typeof DEAL_TYPES)[number];

export const DEAL_STATUSES = [
  // shared entry
  'DRAFT',
  'SUBMITTED',
  'VALIDATED',
  'REFERRED',
  'REJECTED',
  // confirmation
  'AWAITING_CONFIRMATION',
  'CONFIRMED',
  'PARTIALLY_CONFIRMED',
  'DISPUTED',
  // funding
  'ROUTED',
  'OFFER_ACCEPTED',
  'FINANCED',
  // repayment
  'OUTSTANDING',
  'OVERDUE',
  'RECOURSE',
  'SETTLED',
  'WRITTEN_OFF',
  'CANCELLED',
] as const;
export type DealStatus = (typeof DEAL_STATUSES)[number];

/** Position on the 8-stage progress rail shown in the UI. */
export const PIPELINE_STAGES = [
  'Submitted',
  'Validated & Rated',
  'Sent for Confirmation',
  'Confirmed',
  'Routed',
  'Financed & Disbursed',
  'Outstanding',
  'Settled & Closed',
] as const;

export interface StatusMeta {
  label: string;
  /** Index into PIPELINE_STAGES */
  stage: number;
  /** Rail is diverted rather than advancing — render as an off-ramp */
  offRamp: boolean;
  /** Deal is finished; no further transitions except administrative ones */
  terminal: boolean;
  tone: 'neutral' | 'info' | 'positive' | 'warning' | 'danger';
}

export const STATUS_META: Record<DealStatus, StatusMeta> = {
  DRAFT:                 { label: 'Draft',                 stage: 0, offRamp: false, terminal: false, tone: 'neutral'  },
  SUBMITTED:             { label: 'Submitted',             stage: 0, offRamp: false, terminal: false, tone: 'neutral'  },
  VALIDATED:             { label: 'Validated & Rated',     stage: 1, offRamp: false, terminal: false, tone: 'info'     },
  REFERRED:              { label: 'Referred',              stage: 1, offRamp: true,  terminal: false, tone: 'neutral'  },
  REJECTED:              { label: 'Rejected',              stage: 1, offRamp: true,  terminal: true,  tone: 'danger'   },
  AWAITING_CONFIRMATION: { label: 'Sent for Confirmation', stage: 2, offRamp: false, terminal: false, tone: 'info'     },
  CONFIRMED:             { label: 'Confirmed',             stage: 3, offRamp: false, terminal: false, tone: 'positive' },
  PARTIALLY_CONFIRMED:   { label: 'Confirmed (Partial)',   stage: 3, offRamp: false, terminal: false, tone: 'positive' },
  DISPUTED:              { label: 'Disputed',              stage: 2, offRamp: true,  terminal: false, tone: 'danger'   },
  ROUTED:                { label: 'Routed',                stage: 4, offRamp: false, terminal: false, tone: 'info'     },
  OFFER_ACCEPTED:        { label: 'Offer Accepted',        stage: 4, offRamp: false, terminal: false, tone: 'info'     },
  FINANCED:              { label: 'Financed & Disbursed',  stage: 5, offRamp: false, terminal: false, tone: 'positive' },
  OUTSTANDING:           { label: 'Outstanding',           stage: 6, offRamp: false, terminal: false, tone: 'warning'  },
  OVERDUE:               { label: 'Overdue',               stage: 6, offRamp: true,  terminal: false, tone: 'danger'   },
  RECOURSE:              { label: 'Recourse Invoked',      stage: 6, offRamp: true,  terminal: false, tone: 'danger'   },
  SETTLED:               { label: 'Settled & Closed',      stage: 7, offRamp: false, terminal: true,  tone: 'positive' },
  WRITTEN_OFF:           { label: 'Written Off',           stage: 7, offRamp: true,  terminal: true,  tone: 'danger'   },
  CANCELLED:             { label: 'Cancelled',             stage: 0, offRamp: true,  terminal: true,  tone: 'neutral'  },
};

/**
 * Domain actions. Each maps to one endpoint and one audited event; the
 * permission layer grants actions, never raw status writes.
 */
export const DEAL_ACTIONS = [
  'SUBMIT',
  'VALIDATE',
  'APPROVE_REVIEW',
  'REJECT',
  'SEND_FOR_CONFIRMATION',
  'CONFIRM',
  'CONFIRM_PARTIAL',
  'DISPUTE',
  'RESOLVE_DISPUTE',
  'ROUTE',
  'ACCEPT_OFFER',
  'DECLINE_OFFER',
  'DISBURSE',
  'MARK_OUTSTANDING',
  'MARK_OVERDUE',
  'INVOKE_RECOURSE',
  'SETTLE',
  'WRITE_OFF',
  'CANCEL',
] as const;
export type DealAction = (typeof DEAL_ACTIONS)[number];

export interface Transition {
  action: DealAction;
  from: DealStatus[];
  to: DealStatus;
  /** Only applies to this deal type; omitted = both */
  dealType?: DealType;
  /** Human description used in the audit trail and the API docs */
  description: string;
}

export const TRANSITIONS: Transition[] = [
  { action: 'SUBMIT', from: ['DRAFT'], to: 'SUBMITTED',
    description: 'SME submits the receivable or purchase order for financing' },

  { action: 'VALIDATE', from: ['SUBMITTED'], to: 'VALIDATED',
    description: 'Automated checks pass and a credit score is assigned' },
  { action: 'VALIDATE', from: ['SUBMITTED'], to: 'REFERRED',
    description: 'Checks are inconclusive — routed to a human underwriter' },

  { action: 'APPROVE_REVIEW', from: ['REFERRED'], to: 'VALIDATED',
    description: 'Underwriter overrides the referral and approves' },
  { action: 'REJECT', from: ['SUBMITTED', 'VALIDATED', 'REFERRED', 'DISPUTED'], to: 'REJECTED',
    description: 'Deal is declined and returned to the SME with a reason' },

  { action: 'SEND_FOR_CONFIRMATION', from: ['VALIDATED'], to: 'AWAITING_CONFIRMATION',
    description: 'Payer is asked to confirm the debt via portal, OTP link or ERP integration' },

  { action: 'CONFIRM', from: ['AWAITING_CONFIRMATION'], to: 'CONFIRMED',
    description: 'Payer confirms the full amount is payable' },
  { action: 'CONFIRM_PARTIAL', from: ['AWAITING_CONFIRMATION'], to: 'PARTIALLY_CONFIRMED',
    description: 'Payer confirms a reduced amount — the financeable base is written down' },
  { action: 'DISPUTE', from: ['AWAITING_CONFIRMATION', 'CONFIRMED', 'PARTIALLY_CONFIRMED'], to: 'DISPUTED',
    description: 'Payer queries the invoice — funding is frozen' },
  { action: 'RESOLVE_DISPUTE', from: ['DISPUTED'], to: 'CONFIRMED',
    description: 'Dispute resolved in the supplier favour — funding resumes' },

  { action: 'ROUTE', from: ['CONFIRMED', 'PARTIALLY_CONFIRMED'], to: 'ROUTED',
    description: 'Deal is assigned or auctioned to one or more funders' },
  { action: 'ACCEPT_OFFER', from: ['ROUTED'], to: 'OFFER_ACCEPTED',
    description: 'SME accepts the advance rate and fee offered' },
  { action: 'DECLINE_OFFER', from: ['ROUTED'], to: 'CONFIRMED',
    description: 'SME declines — the deal returns to the routing pool' },

  { action: 'DISBURSE', from: ['OFFER_ACCEPTED'], to: 'FINANCED',
    description: 'Advance is paid to the SME and the ledger is posted' },
  { action: 'MARK_OUTSTANDING', from: ['FINANCED'], to: 'OUTSTANDING',
    description: 'Advance is drawn and awaiting payer settlement' },
  { action: 'MARK_OVERDUE', from: ['OUTSTANDING'], to: 'OVERDUE',
    description: 'Due date passed without settlement' },
  { action: 'INVOKE_RECOURSE', from: ['OVERDUE'], to: 'RECOURSE',
    description: 'Recourse programme — the debt is called back to the SME' },

  { action: 'SETTLE', from: ['FINANCED', 'OUTSTANDING', 'OVERDUE', 'RECOURSE'], to: 'SETTLED',
    description: 'Funds received in full; advance, fee and surplus are cleared' },
  { action: 'WRITE_OFF', from: ['OVERDUE', 'RECOURSE'], to: 'WRITTEN_OFF',
    description: 'Debt deemed unrecoverable and charged off' },
  { action: 'CANCEL', from: ['DRAFT', 'SUBMITTED', 'VALIDATED', 'REFERRED'], to: 'CANCELLED',
    description: 'Withdrawn before any funding commitment exists' },
];

export class IllegalTransitionError extends Error {
  constructor(
    readonly from: DealStatus,
    readonly action: DealAction,
    readonly dealType: DealType,
  ) {
    super(`Cannot ${action} a ${dealType} deal in status ${from}`);
    this.name = 'IllegalTransitionError';
  }
}

/** Every transition legal from `status` for a deal of `type`. */
export function allowedTransitions(status: DealStatus, type: DealType): Transition[] {
  return TRANSITIONS.filter(
    (t) => t.from.includes(status) && (!t.dealType || t.dealType === type),
  );
}

/** The actions a deal in this state can take, for driving UI buttons. */
export function allowedActions(status: DealStatus, type: DealType): DealAction[] {
  return [...new Set(allowedTransitions(status, type).map((t) => t.action))];
}

export function canTransition(status: DealStatus, action: DealAction, type: DealType): boolean {
  return allowedTransitions(status, type).some((t) => t.action === action);
}

/**
 * Resolve the target status. `variant` disambiguates the two VALIDATE
 * outcomes (VALIDATED vs REFERRED), which is the only branching action.
 */
export function nextStatus(
  status: DealStatus,
  action: DealAction,
  type: DealType,
  variant?: DealStatus,
): DealStatus {
  const candidates = allowedTransitions(status, type).filter((t) => t.action === action);
  if (candidates.length === 0) throw new IllegalTransitionError(status, action, type);
  if (candidates.length === 1) return candidates[0]!.to;
  const picked = candidates.find((t) => t.to === variant);
  if (!picked) {
    throw new Error(
      `${action} from ${status} is ambiguous — expected one of ${candidates
        .map((c) => c.to)
        .join(', ')}`,
    );
  }
  return picked.to;
}

export function isTerminal(status: DealStatus): boolean {
  return STATUS_META[status].terminal;
}

/** Statuses where funds are at risk on the platform or a lender book. */
export const EXPOSED_STATUSES: DealStatus[] = [
  'FINANCED',
  'OUTSTANDING',
  'OVERDUE',
  'RECOURSE',
];

/**
 * Actions the platform may take on its own, with no human behind them.
 *
 * Deliberately a short allowlist rather than "anything the state machine
 * permits": a background job that could reach DISBURSE or WRITE_OFF would be
 * moving money unattended. Everything here only relabels where a deal already
 * is — the money moved when it was disbursed, and moves again when it settles.
 */
export const AUTOMATIC_ACTIONS: DealAction[] = ['MARK_OUTSTANDING', 'MARK_OVERDUE'];

/**
 * The actions that walk a funded deal from `status` to OVERDUE, or an empty
 * array when it should not be walked there at all.
 *
 * Derived from the declared transitions rather than hard-coded, so adding a
 * status between FINANCED and OVERDUE does not silently leave the overdue
 * sweep stepping over it. A route is only returned if every action on it is
 * in `AUTOMATIC_ACTIONS`, which keeps a future money-moving transition from
 * becoming reachable by a background job just because it shortens the path.
 */
export function pathToOverdue(status: DealStatus, type: DealType): DealAction[] {
  if (status === 'OVERDUE') return [];

  const queue: Array<{ at: DealStatus; via: DealAction[] }> = [{ at: status, via: [] }];
  const seen = new Set<DealStatus>([status]);

  while (queue.length > 0) {
    const { at, via } = queue.shift()!;
    for (const t of allowedTransitions(at, type)) {
      if (!AUTOMATIC_ACTIONS.includes(t.action) || seen.has(t.to)) continue;
      const route = [...via, t.action];
      if (t.to === 'OVERDUE') return route;
      seen.add(t.to);
      queue.push({ at: t.to, via: route });
    }
  }

  return [];
}
